import React, { useState } from 'react';
import { ArrowLeft, UserCheck, Send, CheckCircle, AlertCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { Button } from './ui/button';
import { useTrackLegalPageView } from '../hooks/useTrackLegalPageView';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const DataRequest = () => {
  const { isDarkMode } = useTheme();
  
  // Track page view
  useTrackLegalPageView('data-request');
  
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    requestType: '',
    additionalInfo: '',
    confirmIdentity: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success' | 'error' | null

  const requestTypes = [
    { value: 'access', label: 'Access my data', description: 'Request a copy of all personal data we hold about you' },
    { value: 'rectification', label: 'Correct my data', description: 'Request correction of inaccurate personal data' },
    { value: 'erasure', label: 'Delete my data', description: 'Request deletion of your personal data ("right to be forgotten")' },
    { value: 'restriction', label: 'Restrict processing', description: 'Request limitation on how we process your data' },
    { value: 'portability', label: 'Data portability', description: 'Receive your data in a portable format' },
    { value: 'objection', label: 'Object to processing', description: 'Object to processing based on legitimate interests' },
    { value: 'withdraw', label: 'Withdraw consent', description: 'Withdraw previously given consent for data processing' }
  ];

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const response = await fetch(`${BACKEND_URL}/api/data-request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          submitted_at: new Date().toISOString()
        })
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          fullName: '',
          email: '',
          requestType: '',
          additionalInfo: '',
          confirmIdentity: false
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Error submitting data request:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={`min-h-screen transition-colors duration-300 ${isDarkMode ? 'bg-gray-900' : 'bg-white'}`}>
      {/* Header */}
      <header className={`sticky top-0 z-50 border-b ${isDarkMode ? 'bg-gray-900/95 border-gray-800' : 'bg-white/95 border-gray-200'} backdrop-blur-sm`}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4">
          <Link 
            to="/" 
            className={`inline-flex items-center gap-2 text-sm font-medium ${isDarkMode ? 'text-gray-300 hover:text-white' : 'text-gray-600 hover:text-gray-900'} transition-colors`}
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-2xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
        {/* Title */}
        <div className="text-center mb-10">
          <div className={`inline-flex items-center justify-center w-16 h-16 rounded-2xl mb-6 ${isDarkMode ? 'bg-purple-500/20' : 'bg-purple-100'}`}>
            <UserCheck className={`w-8 h-8 ${isDarkMode ? 'text-purple-400' : 'text-purple-600'}`} />
          </div>
          <h1 className={`text-3xl sm:text-4xl font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            Data Subject Request
          </h1>
          <p className={`text-base ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            Exercise your data protection rights under UK GDPR
          </p>
        </div>

        {/* Info Box */}
        <div className={`p-5 rounded-xl mb-8 ${isDarkMode ? 'bg-blue-500/10 border border-blue-500/20' : 'bg-blue-50 border border-blue-100'}`}>
          <h3 className={`font-semibold mb-2 ${isDarkMode ? 'text-blue-400' : 'text-blue-700'}`}>Your Rights</h3>
          <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
            Under UK GDPR, you have the right to access, correct, delete, or transfer your personal data. We will respond to your request within 30 days. In some cases, we may need to verify your identity before processing your request.
          </p>
        </div>

        {/* Success Message */}
        {submitStatus === 'success' && (
          <div className={`p-5 rounded-xl mb-8 ${isDarkMode ? 'bg-green-500/10 border border-green-500/20' : 'bg-green-50 border border-green-200'}`}>
            <div className="flex items-start gap-3">
              <CheckCircle className={`w-6 h-6 flex-shrink-0 ${isDarkMode ? 'text-green-400' : 'text-green-600'}`} />
              <div>
                <h3 className={`font-semibold mb-1 ${isDarkMode ? 'text-green-400' : 'text-green-700'}`}>Request Submitted Successfully</h3>
                <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  We have received your data request. You will receive a confirmation email shortly. We will process your request within 30 days as required by UK GDPR.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Error Message */}
        {submitStatus === 'error' && (
          <div className={`p-5 rounded-xl mb-8 ${isDarkMode ? 'bg-red-500/10 border border-red-500/20' : 'bg-red-50 border border-red-200'}`}>
            <div className="flex items-start gap-3">
              <AlertCircle className={`w-6 h-6 flex-shrink-0 ${isDarkMode ? 'text-red-400' : 'text-red-600'}`} />
              <div>
                <h3 className={`font-semibold mb-1 ${isDarkMode ? 'text-red-400' : 'text-red-700'}`}>Submission Failed</h3>
                <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  There was an error submitting your request. Please try again or contact us directly at PeterG@neura-ai.info
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Full Name */}
          <div>
            <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              Full Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="fullName"
              value={formData.fullName}
              onChange={handleInputChange}
              required
              placeholder="Enter your full legal name"
              className={`w-full px-4 py-3 rounded-xl border transition-colors ${
                isDarkMode 
                  ? 'bg-gray-800 border-gray-700 text-white placeholder-gray-500 focus:border-cyan-500' 
                  : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:border-cyan-500'
              } focus:outline-none focus:ring-2 focus:ring-cyan-500/20`}
            />
          </div>

          {/* Email */}
          <div>
            <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              Email Address <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              required
              placeholder="The email address associated with your account"
              className={`w-full px-4 py-3 rounded-xl border transition-colors ${
                isDarkMode 
                  ? 'bg-gray-800 border-gray-700 text-white placeholder-gray-500 focus:border-cyan-500' 
                  : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:border-cyan-500'
              } focus:outline-none focus:ring-2 focus:ring-cyan-500/20`}
            />
          </div>

          {/* Request Type */}
          <div>
            <label className={`block text-sm font-medium mb-3 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              Type of Request <span className="text-red-500">*</span>
            </label>
            <div className="space-y-3">
              {requestTypes.map((type) => (
                <label 
                  key={type.value}
                  className={`flex items-start gap-3 p-4 rounded-xl border cursor-pointer transition-all ${
                    formData.requestType === type.value
                      ? isDarkMode 
                        ? 'border-cyan-500 bg-cyan-500/10' 
                        : 'border-cyan-500 bg-cyan-50'
                      : isDarkMode
                        ? 'border-gray-700 hover:border-gray-600'
                        : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <input
                    type="radio"
                    name="requestType"
                    value={type.value}
                    checked={formData.requestType === type.value}
                    onChange={handleInputChange}
                    required
                    className="mt-1 w-4 h-4 text-cyan-500 focus:ring-cyan-500"
                  />
                  <div>
                    <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{type.label}</p>
                    <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{type.description}</p>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* Additional Information */}
          <div>
            <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              Additional Information
            </label>
            <textarea
              name="additionalInfo"
              value={formData.additionalInfo}
              onChange={handleInputChange}
              rows={4}
              placeholder="Please provide any additional details that may help us process your request (e.g., specific data you're looking for, account details, etc.)"
              className={`w-full px-4 py-3 rounded-xl border transition-colors resize-none ${
                isDarkMode 
                  ? 'bg-gray-800 border-gray-700 text-white placeholder-gray-500 focus:border-cyan-500' 
                  : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:border-cyan-500'
              } focus:outline-none focus:ring-2 focus:ring-cyan-500/20`}
            />
          </div>

          {/* Identity Confirmation */}
          <div className={`p-5 rounded-xl ${isDarkMode ? 'bg-gray-800' : 'bg-gray-50'}`}>
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                name="confirmIdentity"
                checked={formData.confirmIdentity}
                onChange={handleInputChange}
                required
                className="mt-1 w-5 h-5 rounded text-cyan-500 focus:ring-cyan-500"
              />
              <div>
                <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  Identity Confirmation <span className="text-red-500">*</span>
                </p>
                <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  I confirm that I am the data subject (or authorised to act on their behalf) and that the information provided is accurate. I understand that NEURA AI SOFTWARES may need to verify my identity before processing this request.
                </p>
              </div>
            </label>
          </div>

          {/* Submit Button */}
          <Button
            type="submit"
            disabled={isSubmitting}
            className="w-full bg-gradient-to-r from-cyan-500 to-purple-500 hover:from-cyan-600 hover:to-purple-600 text-white font-semibold py-4 rounded-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? (
              <span className="flex items-center justify-center gap-2">
                <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Submitting...
              </span>
            ) : (
              <span className="flex items-center justify-center gap-2">
                <Send className="w-5 h-5" />
                Submit Request
              </span>
            )}
          </Button>
        </form>

        {/* Processing Info */}
        <div className={`mt-8 p-5 rounded-xl ${isDarkMode ? 'bg-gray-800/50' : 'bg-gray-50'}`}>
          <h3 className={`font-semibold mb-3 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>What happens next?</h3>
          <ol className={`list-decimal pl-5 space-y-2 text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            <li>You will receive an email confirming we have received your request</li>
            <li>We may contact you to verify your identity if necessary</li>
            <li>We will process your request within 30 days (as required by UK GDPR)</li>
            <li>If we need more time (up to 60 additional days for complex requests), we will inform you</li>
            <li>You will receive a detailed response to your request via email</li>
          </ol>
        </div>

        {/* Back to Home */}
        <div className="mt-12 pt-8 border-t border-gray-200 dark:border-gray-700 text-center">
          <Link 
            to="/" 
            className={`inline-flex items-center gap-2 px-6 py-3 rounded-full font-medium transition-colors ${isDarkMode ? 'bg-gray-800 text-white hover:bg-gray-700' : 'bg-gray-100 text-gray-900 hover:bg-gray-200'}`}
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
        </div>
      </main>

      {/* Footer */}
      <footer className={`border-t py-6 ${isDarkMode ? 'border-gray-800' : 'border-gray-200'}`}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <p className={`text-sm ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
            © {new Date().getFullYear()} NEURA AI SOFTWARES. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default DataRequest;
